import { useState } from "react";
import { MILESTONES } from "./crmPipelineShared";
import { DateField, localToday } from "./DateField";

const field = "w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3 py-2 text-sm text-white outline-none";
const today = () => localToday();
const num = (v) => (v === "" || v === null || v === undefined ? undefined : parseFloat(v));

function F({ label, children }) {
  return <div><label className="block text-xs text-emerald-100/60 mb-1">{label}</label>{children}</div>;
}

// Captures milestone details when moving a vendor into a referral stage.
export function ReferralStageDialog({ stage, hasReferral, onCancel, onConfirm }) {
  const ms = MILESTONES.find(([s]) => s === stage);
  const isReferral = stage === "Referred to Auction Partner";
  const [f, setF] = useState({ milestone_date: today(), referral_date: "", partner: "Pattinsons", auction_contact: "", property_address: "",
    asking_price: "", sale_price: "", received_commission: "", expected_commission: "", note: "", next_follow_up: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const creating = !hasReferral;

  const submit = () => {
    const r = { milestone_date: f.milestone_date, note: f.note, next_follow_up: f.next_follow_up };
    if (creating) Object.assign(r, { partner: f.partner, auction_contact: f.auction_contact, property_address: f.property_address,
      asking_price: num(f.asking_price), referral_date: isReferral ? f.milestone_date : f.referral_date });
    if (stage === "Sold / Awaiting Completion" || stage === "Sale Completed") r.sale_price = num(f.sale_price);
    if (stage === "Commission Received") r.received_commission = num(f.received_commission);
    if (f.expected_commission !== "") r.expected_commission = num(f.expected_commission);
    Object.keys(r).forEach((k) => r[k] === undefined && delete r[k]);
    onConfirm({ referral: r });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="referral-stage-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative w-full max-w-md bg-[#0B241B] border border-white/10 rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="text-base font-serif text-white">{stage}</h3>
        <p className="text-xs text-emerald-100/50 mt-1 mb-4">
          {isReferral ? "Counts once as a successful CRM referral conversion. This is not a sale and not commission." : "Records this milestone on the existing referral — it won't be counted as a new referral."}
        </p>
        <div className="space-y-3">
          <F label={`${ms ? ms[2] : "Milestone"} date`}><DateField value={f.milestone_date} onChange={(v) => setF((x) => ({ ...x, milestone_date: v }))} testId="ref-milestone-date" /></F>
          {creating && (
            <>
              {!isReferral && <F label="Original referral date (leave blank if unknown)"><DateField value={f.referral_date} onChange={(v) => setF((x) => ({ ...x, referral_date: v }))} testId="ref-referral-date" /></F>}
              <div className="grid grid-cols-2 gap-3">
                <F label="Auction partner"><input value={f.partner} onChange={set("partner")} data-testid="ref-partner" className={field} /></F>
                <F label="Auction contact"><input value={f.auction_contact} onChange={set("auction_contact")} data-testid="ref-auction-contact" className={field} /></F>
              </div>
              <F label="Property address (defaults to the lead's address)"><input value={f.property_address} onChange={set("property_address")} data-testid="ref-address" className={field} /></F>
              <F label="Asking / guide price (£) — expected commission is 1%"><input type="number" value={f.asking_price} onChange={set("asking_price")} data-testid="ref-asking" className={field} /></F>
            </>
          )}
          {(stage === "Sold / Awaiting Completion" || stage === "Sale Completed") && <F label="Sale price (£)"><input type="number" value={f.sale_price} onChange={set("sale_price")} data-testid="ref-sale-price" className={field} /></F>}
          {stage === "Commission Received" && <F label="Commission received (£)"><input type="number" value={f.received_commission} onChange={set("received_commission")} data-testid="ref-received" className={field} /></F>}
          <F label="Override expected commission (£, optional)"><input type="number" value={f.expected_commission} onChange={set("expected_commission")} data-testid="ref-expected-override" className={field} /></F>
          <F label="Next follow-up (optional)"><DateField value={f.next_follow_up} onChange={(v) => setF((x) => ({ ...x, next_follow_up: v }))} testId="ref-followup" /></F>
          <F label="Referral note (optional)"><textarea rows={2} value={f.note} onChange={set("note")} data-testid="ref-note" className={field} /></F>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="flex-1 rounded-full py-2.5 text-sm bg-white/5 text-emerald-100/80 hover:bg-white/10">Cancel</button>
          <button onClick={submit} data-testid="ref-stage-confirm" className="flex-1 rounded-full py-2.5 text-sm bg-brass text-emerald-950 font-semibold">Save stage</button>
        </div>
      </div>
    </div>
  );
}
